import { faRectangleVerticalHistory } from "@fortawesome/pro-regular-svg-icons/faRectangleVerticalHistory";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, CardContent, IconButton, useTheme } from '@mui/material';
import { MprcCard, MprcContainer, MprcInfiniteLoading, MprcRequestErrorReport, MprcText } from 'components';
import { FeedItemDto, FeedType } from 'generated/CoreApi_v1';
import { usePageTitle } from "hooks/usePageTitle";
import { useWindowSize } from "hooks/useWindowSize";
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from "react-i18next";
import { Link, useLocation, useParams } from "react-router";
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { useGobackOrKnowledge } from "views/common/hooks/useGobackOrHome";
import { CommentBox } from 'views/knowledge/components/comment/CommentBox';
import { subscribe, unsubscribe } from "views/knowledge/feed/FeedEvents";
import { PublicationContentBox } from "views/knowledge/publication/pages/components/PublicationContentBox";
import { RightPanelContentPage } from "../common/RightPanelContentPage";
import { PublicationCardHeader } from '../components/PublicationCardHeader';
import { PublicationHeader } from "../components/PublicationHeader";
import { PostThreads } from "./components/PostThreads";

////////////////////////////////////////////////////////////////////////////////////////////////

export const PostViewer: React.FC = () => {

    const { id } = useParams();
    const location = useLocation();
    const gobackOrHome = useGobackOrKnowledge();

    const api = useCoreApi();
    const theme = useTheme();
    const { t } = useTranslation("knowledge");
    const { width } = useWindowSize({ wait: 50 });

    const [loading, setLoading] = useState<boolean>(true);
    const [publication, setPublication] = useState<FeedItemDto>();
    const [viewError, setViewError] = useState<any>();
    const [openRightPanel, setOpenRightPanel] = useState<boolean>(false);
    const publicationId = id;

    usePageTitle(t("Feed.Post.PageTitle"));

    ///
    const load = (publicationId: string) => {
        let markRead = true;

        // Se a solicitação foi realizada a partir do Feed de Histórico,
        // não deve marcar a Publicação como 'Lida', para não alterar o histórico de visualização.
        if (location.state?.hasOwnProperty("previousPathname")) {
            const previousPathname = location.state.previousPathname as string;
            markRead = !previousPathname.startsWith(KnowledgePaths.FeedHistory);
        }

        setLoading(true);
        api.v1.knowledgeFeed.getFeedItem({ publicationId, markRead })
            .then((result) => {
                setPublication(result.data);
                setLoading(false);
            })
            .catch((error: any) => {
                setViewError(error);
                setLoading(false);
            });
    };

    ///
    const onDataItemChanged = (data: Partial<FeedItemDto>) => {
        setPublication(prevState => {
            return { ...prevState!, ...data };
        });
    };

    ///
    useEffect(() => {
        if (publicationId != null) {
            load(publicationId);
        }

        subscribe("PublicationRestored", () => load(publicationId!));
        subscribe("PublicationPermanentlyDeleted", gobackOrHome);
        return () => {
            unsubscribe("PublicationRestored", () => load(publicationId!));
            unsubscribe("PublicationPermanentlyDeleted", gobackOrHome);
        };
    }, [publicationId]);

    useEffect(() => {
        if (openRightPanel && width > theme.breakpoints.values["md"])
            setOpenRightPanel(false);
    }, [width]);

    ////////////////////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////////////////////

    const RenderPublication = useCallback(() => (
        <PublicationCardHeader
            feedType={FeedType.ContentPage}
            publication={publication!}
            onNeedResetRowHeight={() => { }}
            onReload={() => load(publication!.publicationId)}
            onFeedItemChanged={(data) => {
                if (data.deletedOn) gobackOrHome();
                else onDataItemChanged(data);
            }}
        />
    ), [publication]);

    ///
    const RenderThread = useCallback(() => {
        return <PostThreads publicationId={publicationId!} onError={e => setViewError(e)}></PostThreads>;
    }, [publicationId]);

    ///
    const RenderComment = useMemo(() => {
        return (
            <Box mt={1}>
                <MprcCard>
                    <CardContent>
                        <CommentBox publicationId={publicationId!} publicationTitle={publication?.title ?? ""} readOnly={publication?.readOnly ?? false} />
                    </CardContent>
                </MprcCard>
            </Box>
        );
    }, [publication?.publicationId, publication?.readOnly]);

    ///
    const headerActions = useMemo(() => {
        var headerItems: React.ReactNode[] = [];

        if (publication?.listExclusive != null) {
            headerItems.push(
                <Link to={KnowledgePaths.FeedList(publication.listExclusive!.listId)} title={`${t("Publication.Common.Header.GoToExclusiveList")}: ${publication.listExclusive.title}`}>
                    <MprcText color="warning" >
                        {t("Publication.Common.Header.ExclusiveListText")}
                    </MprcText>
                </Link>
            );
        }

        if (width < theme.breakpoints.values["md"])
            headerItems.push([<IconButton onClick={() => setOpenRightPanel(state => !state)}><FontAwesomeIcon icon={faRectangleVerticalHistory} /></IconButton>]);

        return headerItems;

    }, [publication, width]);


    return (
        <>
            {
                viewError != undefined
                    ? <MprcRequestErrorReport error={viewError} />
                    : loading
                        ? <MprcInfiniteLoading visible />
                        : publication != undefined &&
                        <>
                            <MprcContainer position="sticky" margin="none">
                                <PublicationHeader
                                    onClose={gobackOrHome}
                                    actions={headerActions}
                                />
                            </MprcContainer>

                            <PublicationContentBox
                                id="pubContent"
                                openRightPanel={openRightPanel}
                                maxWidth="lg"
                                onCloseRighPanel={() => setOpenRightPanel(false)}
                                content={
                                    <MprcContainer>
                                        {RenderPublication()}
                                        {RenderThread()}
                                        {RenderComment}
                                    </MprcContainer>
                                }
                                rightPanel={publication != undefined ? RightPanelContentPage(publication) : undefined}
                            />
                        </>
            }
        </>
    );
};
